// src/components/PatientFeedback.js
import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/api";
import PatientNavbar from "../components/PatientNavbar";
import {
  Box, Typography, TextField, Button, Rating, CircularProgress, Alert, Paper, useMediaQuery, useTheme
} from "@mui/material";
import "./PatientFeedback.css";

const PatientFeedback = () => {
  const { appointmentId } = useParams();
  const { patientUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [appointment, setAppointment] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchAppointment = async () => {
      if (!patientUser || patientUser.role !== "patient") {
        setError("You are not authorized to submit feedback. Please log in as a patient.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/appointments/${appointmentId}`, {
          headers: { Authorization: `Bearer ${patientUser.accessToken}` },
        });
        console.log("Fetched appointment data:", response.data);
        setAppointment(response.data);
      } catch (err) {
        console.error("Failed to load appointment:", err.response?.data || err.message);
        setError("Failed to load appointment: " + (err.response?.data?.message || err.message));
      } finally {
        setIsLoading(false);
      }
    };
    fetchAppointment();
  }, [appointmentId, patientUser]);

  const handleSubmitFeedback = async () => {
    if (!patientUser) {
      setError("Please log in to submit feedback.");
      return;
    }

    if (!appointment) {
      setError("Appointment data not loaded. Please try again.");
      return;
    }

    let doctorId = null;
    let patientId = null;

    try {
      doctorId = typeof appointment.doctorId === "object" ? appointment.doctorId._id : appointment.doctorId;
      patientId = typeof appointment.patientId === "object" ? appointment.patientId._id : appointment.patientId;

      if (!doctorId || !patientId) {
        throw new Error("Doctor or patient ID is missing from appointment data.");
      }

      if (rating === 0) {
        setError("Please provide a rating.");
        return;
      }

      const feedbackData = {
        appointmentId,
        doctorId,
        patientId,
        rating,
        feedback,
      };
      console.log("Submitting feedback:", feedbackData);

      await api.post("/feedback", feedbackData, {
        headers: { Authorization: `Bearer ${patientUser.accessToken}` },
      });
      console.log("Feedback submitted successfully");
      navigate(`/feedback-success?appointmentId=${appointmentId}`);
    } catch (err) {
      console.error("Failed to submit feedback:", err.response?.data || err.message);
      setError("Failed to submit feedback: " + (err.response?.data?.message || err.message));
    }
  };

  const handleBackToDashboard = () => {
    navigate(`/patient-dashboard/${patientUser?.id || ""}`);
  };

  if (isLoading) {
    return (
      <Box className="patient-feedback-page">
        <PatientNavbar />
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="patient-feedback-page">
        <PatientNavbar />
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, maxWidth: "600px", mx: "auto", textAlign: "center" }}>
          <Alert severity="error">{error}</Alert>
          <Button
            variant="contained"
            onClick={handleBackToDashboard}
            sx={{ mt: 2, bgcolor: "#007bff", "&:hover": { bgcolor: "#0056b3" } }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="patient-feedback-page">
      <PatientNavbar />
      <Box
        component="main"
        sx={{
          flex: 1,
          p: { xs: 2, sm: 3 },
          maxWidth: { xs: "100%", sm: "600px" },
          mx: "auto",
          textAlign: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
            Provide Feedback
          </Typography>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            Rate your consultation with Dr. {appointment?.doctorId?.name || "Unknown Doctor"}
          </Typography>
          <Box className="appointment-details" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Date:</strong> {new Date(appointment?.date).toLocaleString()}
            </Typography>
          </Box>

          <Box className="rating-section" sx={{ mb: 3 }}>
            <Typography component="legend" sx={{ mb: 1 }}>
              Rating (1-5)
            </Typography>
            <Rating
              name="patient-rating"
              value={rating}
              onChange={(event, newValue) => setRating(newValue)}
              size={isMobile ? "medium" : "large"}
            />
          </Box>

          <Box className="feedback-section" sx={{ mb: 3 }}>
            <TextField
              label="Feedback"
              multiline
              rows={isMobile ? 4 : 5}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              fullWidth
              placeholder="Share your experience..."
              variant="outlined"
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmitFeedback}
            disabled={rating === 0}
            sx={{
              bgcolor: "#007bff",
              "&:hover": { bgcolor: "#0056b3" },
              width: { xs: "100%", sm: "auto" },
              py: 1,
            }}
          >
            Submit Feedback
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default PatientFeedback;